/* TRAPT v0.9.8.7.6 — v0.9.3 direct bidirectional scrub with stable phone geometry */
(() => {
  document.documentElement.classList.add('t8-js');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 760px)');

  const initMenus = root => {
    root.querySelectorAll('[data-t4-menu-toggle]').forEach(button => {
      if (button.dataset.t4Ready) return;
      button.dataset.t4Ready = 'true';
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      const header = button.closest('.t4-header');
      const shell = button.closest('.t992-header-shell') || header?.parentElement;
      const scrim = shell?.querySelector('[data-t4-menu-scrim]');
      if (!panel || !header || !shell || !scrim) return;

      const syncTop = () => {
        shell.style.setProperty('--t7728-drawer-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
      };
      // Hard scroll lock: overflow alone does not hold on mobile, so the body is pinned
      // at its current offset and restored on close.
      let lockedScrollY = 0;
      const lockScroll = (open) => {
        const root = document.documentElement;
        if (open) {
          lockedScrollY = window.scrollY || window.pageYOffset || 0;
          root.classList.add('t7728-scroll-lock');
          document.body.style.top = `-${lockedScrollY}px`;
          return;
        }
        if (!root.classList.contains('t7728-scroll-lock')) return;
        root.classList.remove('t7728-scroll-lock');
        document.body.style.top = '';
        window.scrollTo(0, lockedScrollY);
      };

      const setOpen = open => {
        if (open) syncTop();
        panel.toggleAttribute('open', open);
        header.classList.toggle('is-menu-open', open);
        shell.classList.toggle('is-menu-open', open);
        button.setAttribute('aria-expanded', String(open));
        button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        scrim.tabIndex = open ? 0 : -1;
        document.body.classList.toggle('t7728-menu-open', open);
        lockScroll(open);
      };

      button.addEventListener('click', () => setOpen(!panel.hasAttribute('open')));
      scrim.addEventListener('click', () => setOpen(false));
      panel.addEventListener('click', event => {
        if (event.target.closest('a')) setOpen(false);
      });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && (panel.hasAttribute('open') || header.classList.contains('is-menu-open'))) {
          setOpen(false);
          button.focus();
        }
      });
      window.addEventListener('resize', () => {
        if (panel.hasAttribute('open')) syncTop();
        if (window.matchMedia('(min-width: 901px)').matches) setOpen(false);
      }, { passive: true });
    });
  };

  // Phones: a normal-height section with a muted, looping video. No scroll scrubbing and no
  // full up-front download, which were slow on mobile (and hid the caption until late in the scroll).
  const setupMobileLoop = (section, video) => {
    section.classList.add('is-mobile-loop');
    section.classList.remove('is-media-fallback', 'is-media-loading');
    const source = video.dataset.mobileSrc || video.dataset.desktopSrc || video.dataset.src;
    const poster = video.dataset.mobilePoster || video.dataset.desktopPoster;
    if (poster) video.poster = poster;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.preload = 'metadata';
    // Show the film as soon as there is a frame to show. Waiting only on 'playing' left the
    // section stuck on its poster whenever autoplay was blocked (in-app browsers, the theme
    // editor preview, low power mode), which read as a broken section.
    const reveal = () => {
      section.classList.add('is-loop-playing');
      section.classList.remove('is-loop-fallback');
    };
    ['loadeddata', 'canplay', 'playing', 'timeupdate'].forEach(event => {
      video.addEventListener(event, reveal);
    });
    video.addEventListener('error', () => {
      section.classList.remove('is-loop-playing');
      section.classList.add('is-loop-fallback');
    });
    if (!source || !('IntersectionObserver' in window)) return;

    video.autoplay = true;
    video.setAttribute('autoplay', '');

    let fallbackTimer = null;
    const tryPlay = () => {
      if (!video.getAttribute('src')) video.src = source;
      const played = video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
      if (fallbackTimer) clearTimeout(fallbackTimer);
      // Nothing decoded after a few seconds: animate the poster instead of sitting still.
      fallbackTimer = setTimeout(() => {
        if (video.readyState < 2) section.classList.add('is-loop-fallback');
        else section.classList.remove('is-loop-fallback');
      }, 3500);
    };

    // Start fetching well before the section arrives, so it is not a static poster on entry.
    const preloader = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) return;
      if (!video.getAttribute('src')) {
        video.src = source;
        video.load();
      }
      preloader.disconnect();
    }, { rootMargin: '150% 0px' });
    preloader.observe(section);

    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        tryPlay();
      } else if (!video.paused) {
        video.pause();
      }
    }, { rootMargin: '10% 0px' });
    observer.observe(section);

    // Browsers that refuse to autoplay usually allow it after any gesture.
    const retryOnGesture = () => {
      if (video.paused || video.readyState < 2) tryPlay();
    };
    ['touchstart', 'pointerdown', 'click', 'scroll'].forEach(event => {
      document.addEventListener(event, retryOnGesture, { passive: true, once: true });
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) retryOnGesture();
    });

    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(section)) {
        observer.disconnect();
        preloader.disconnect();
        if (fallbackTimer) clearTimeout(fallbackTimer);
        video.pause();
      }
    });
  };

  const initDepth = root => {
    root.querySelectorAll('[data-t6-depth]').forEach(section => {
      if (section.dataset.t6Ready) return;
      section.dataset.t6Ready = 'true';
      const video = section.querySelector('[data-t6-depth-video]');
      const label = section.querySelector('[data-t6-depth-label]');
      const sticky = section.querySelector('.t6-depth__sticky');
      if (!video || !sticky) return;

      const resetOpeningFrame = () => {
        section.classList.remove('is-media-ready', 'is-media-loading', 'is-details-visible');
        section.classList.add('is-media-fallback');
        section.style.setProperty('--t6-progress', '0');
        section.style.setProperty('--t6-made', '1');
        section.style.setProperty('--t6-seen', '0');
        section.style.setProperty('--t6-detail', '0');
        if (label) label.innerHTML = '01&nbsp;&nbsp;Close';
      };

      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        section.classList.add('is-reduced-motion');
        resetOpeningFrame();
        video.pause();
        video.removeAttribute('src');
        return;
      }

      // Phones default to the pinned scroll film; the section can opt into the lighter loop.
      if (mobileViewport.matches && section.dataset.mobileMode === 'loop') {
        setupMobileLoop(section, video);
        return;
      }

      let duration = 0;
      let visible = false;
      let queued = false;
      let loading = false;
      let mediaReady = false;
      let failed = false;
      let objectUrl = '';
      let abortController = null;
      let sectionStart = 0;
      let sectionTravel = 1;
      let layoutWidth = 0;
      let layoutHeight = 0;
      let layoutQueued = false;

      const selectedMedia = () => {
        const mobile = mobileViewport.matches;
        return {
          source: mobile
            ? video.dataset.mobileSrc || video.dataset.desktopSrc || video.dataset.src
            : video.dataset.desktopSrc || video.dataset.src || video.dataset.mobileSrc,
          poster: mobile
            ? video.dataset.mobilePoster || video.dataset.desktopPoster
            : video.dataset.desktopPoster || video.dataset.mobilePoster,
          variant: mobile ? 'mobile' : 'desktop'
        };
      };
      const clamp = value => Math.max(0, Math.min(1, value));
      const smoothstep = (start, end, value) => {
        const amount = clamp((value - start) / (end - start));
        return amount * amount * (3 - 2 * amount);
      };
      const revokeObjectUrl = () => {
        if (!objectUrl) return;
        URL.revokeObjectURL(objectUrl);
        objectUrl = '';
      };
      const resetToPoster = () => {
        mediaReady = false;
        resetOpeningFrame();
        section.classList.toggle('is-media-loading', loading);
      };
      const failMedia = () => {
        failed = true;
        loading = false;
        resetToPoster();
        video.pause();
        video.removeAttribute('src');
        video.load();
        revokeObjectUrl();
      };
      const waitForVideo = () => new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error('Depth film decode timed out')), 18000);
        const clean = () => {
          window.clearTimeout(timeout);
          video.removeEventListener('loadeddata', ready);
          video.removeEventListener('error', error);
        };
        const ready = () => {
          clean();
          const value = Number(video.duration);
          if (!Number.isFinite(value) || value <= 0) reject(new Error('Invalid depth film duration'));
          else resolve(value);
        };
        const error = () => { clean(); reject(new Error('Depth film could not be decoded')); };
        video.addEventListener('loadeddata', ready, { once: true });
        video.addEventListener('error', error, { once: true });
        if (video.readyState >= 2) ready();
      });
      const loadMedia = async () => {
        const media = selectedMedia();
        if (!media.source || loading || mediaReady || failed) return;
        loading = true;
        section.dataset.t6MediaVariant = media.variant;
        if (media.poster) video.poster = media.poster;
        resetToPoster();
        try {
          abortController = new AbortController();
          const response = await fetch(media.source, {
            cache: 'force-cache',
            mode: 'cors',
            signal: abortController.signal
          });
          if (!response.ok) throw new Error(`Depth film request failed: ${response.status}`);
          const mediaBlob = await response.blob();
          if (!mediaBlob.size) throw new Error('Depth film response was empty');
          revokeObjectUrl();
          objectUrl = URL.createObjectURL(mediaBlob);
          video.preload = 'auto';
          video.src = objectUrl;
          video.load();
          duration = await waitForVideo();
          video.currentTime = 0;
          loading = false;
          mediaReady = true;
          section.classList.remove('is-media-loading', 'is-media-fallback');
          section.classList.add('is-media-ready');
          measureGeometry(true);
          requestRender();
        } catch (error) {
          if (error.name !== 'AbortError') failMedia();
        }
      };
      const measureGeometry = force => {
        const width = document.documentElement.clientWidth;
        if (!force && Math.abs(width - layoutWidth) < 2) return false;
        const rect = section.getBoundingClientRect();
        layoutWidth = width;
        layoutHeight = window.innerHeight;
        sectionStart = window.scrollY + rect.top;
        sectionTravel = Math.max(1, section.offsetHeight - sticky.offsetHeight);
        return true;
      };
      const render = () => {
        queued = false;
        if (!visible || !mediaReady) return;
        if (!layoutWidth) measureGeometry(true);
        const progress = clamp((window.scrollY - sectionStart) / sectionTravel);
        const made = 1 - smoothstep(.36, .5, progress);
        const seen = smoothstep(.42, .56, progress);
        const detail = smoothstep(.72, .84, progress);
        section.style.setProperty('--t6-progress', progress.toFixed(4));
        section.style.setProperty('--t6-made', made.toFixed(4));
        section.style.setProperty('--t6-seen', seen.toFixed(4));
        section.style.setProperty('--t6-detail', detail.toFixed(4));
        section.classList.toggle('is-details-visible', progress >= .7);
        if (label) {
          label.innerHTML = progress < .42
            ? '01&nbsp;&nbsp;Close'
            : progress < .6 ? '02&nbsp;&nbsp;Straight' : '03&nbsp;&nbsp;Layers';
        }
        if (duration > 0 && video.readyState >= 2) {
          const target = Math.min(duration - .04, duration * progress);
          if (Math.abs(video.currentTime - target) > 1 / 30) video.currentTime = target;
        }
      };
      const requestRender = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(render);
      };
      const requestLayout = () => {
        const widthChanged = Math.abs(document.documentElement.clientWidth - layoutWidth) >= 2;
        const desktopHeightChanged = !mobileViewport.matches && Math.abs(window.innerHeight - layoutHeight) >= 2;
        if ((!widthChanged && !desktopHeightChanged) || layoutQueued) return;
        layoutQueued = true;
        requestAnimationFrame(() => {
          layoutQueued = false;
          measureGeometry(true);
          requestRender();
        });
      };
      // The film is a multi-megabyte download. Start it early enough to be ready when the
      // section arrives, but never while the page is still painting.
      const afterFirstLoad = (run) => {
        if (document.readyState === 'complete') {
          (window.requestIdleCallback || window.setTimeout)(run, { timeout: 1200 });
          return;
        }
        window.addEventListener('load', () => {
          (window.requestIdleCallback || window.setTimeout)(run, { timeout: 1200 });
        }, { once: true });
      };
      const loadObserver = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        afterFirstLoad(() => loadMedia());
        loadObserver.disconnect();
        // One viewport of lead time is enough to have the film ready, and it keeps a
        // multi-megabyte download out of the way of the first paint.
      }, { rootMargin: mobileViewport.matches ? '80% 0px' : '110% 0px' });
      const visibilityObserver = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        if (visible) { loadMedia(); requestRender(); }
      }, { rootMargin: '15% 0px' });
      const watchesWindowResize = !mobileViewport.matches;
      const cleanup = () => {
        loadObserver.disconnect();
        visibilityObserver.disconnect();
        abortController?.abort();
        window.removeEventListener('scroll', requestRender);
        if (watchesWindowResize) window.removeEventListener('resize', requestLayout);
        window.removeEventListener('orientationchange', requestLayout);
        revokeObjectUrl();
      };
      resetToPoster();
      measureGeometry(true);
      loadObserver.observe(section);
      visibilityObserver.observe(section);
      window.addEventListener('scroll', requestRender, { passive: true });
      if (watchesWindowResize) window.addEventListener('resize', requestLayout, { passive: true });
      window.addEventListener('orientationchange', requestLayout, { passive: true });
      // Content above (images, carousels) can change the page height after load; keep the
      // scroll-to-video mapping in step so the film does not drift out of sync.
      window.addEventListener('load', () => { measureGeometry(true); requestRender(); }, { once: true });
      if ('ResizeObserver' in window) {
        let lastPageHeight = document.documentElement.scrollHeight;
        new ResizeObserver(() => {
          const pageHeight = document.documentElement.scrollHeight;
          if (Math.abs(pageHeight - lastPageHeight) < 2) return;
          lastPageHeight = pageHeight;
          measureGeometry(true);
          requestRender();
        }).observe(document.body);
      }
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(section)) cleanup();
      });
    });
  };

  const init = root => { initMenus(root); initDepth(root); };
  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

/* TRAPT v0.9.8.7.8.1.0 — Quick-add modal: hold the page still behind it.
   Dawn only sets overflow on the body, which phones ignore, so the grid kept
   scrolling under the open modal. Pin the body at its offset while any
   quick-add modal is open and put the reader back where they were on close. */
(() => {
  const root = document.documentElement;
  const LOCK = 't7728-scroll-lock';
  let lockedScrollY = 0;
  let locked = false;

  const anyOpen = () => !!document.querySelector('quick-add-modal[open]');

  const setLock = (open) => {
    if (open === locked) return;
    locked = open;
    if (open) {
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      root.classList.add(LOCK);
      document.body.style.top = `-${lockedScrollY}px`;
      return;
    }
    // The menu drawer holds the same lock; never release the page under it.
    if (document.body.classList.contains('t7728-menu-open')) return;
    root.classList.remove(LOCK);
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  };

  // The close button is pinned to the panel, so it would ride over the content
  // as you read. Fade it out as soon as the panel is scrolled, bring it back at
  // the top.
  const watchScroll = (modal) => {
    const panel = modal.querySelector('.quick-add-modal__content');
    const scroller = modal.querySelector('.quick-add-modal__content-info');
    if (!panel || !scroller || scroller.dataset.t810Scroll) return;
    scroller.dataset.t810Scroll = 'true';
    const sync = () => panel.classList.toggle('is-scrolled', scroller.scrollTop > 24);
    scroller.addEventListener('scroll', sync, { passive: true });
    sync();
  };

  // Free scrolling in the modal: mandatory snap points and the theme's own
  // scrollTo kept disagreeing on a phone and left a slide resting between two
  // images. The gallery scrolls freely here and says where it is with a bar.
  // iOS would not hold a CSS snap point here: a programmatic jump to a slide and
  // the snap engine disagreed and left the track resting between two images.
  // The track scrolls freely and we settle it ourselves once the finger is off.
  const settleGallery = (list) => {
    if (!list || list.dataset.t810Settle) return;
    list.dataset.t810Settle = 'true';
    let timer = 0;
    list.addEventListener(
      'scroll',
      () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const slides = [...list.children];
          if (!slides.length) return;
          // Snap to a real slide's offset, not a multiple of the rounded width:
          // fractional widths would drift a pixel or two per slide otherwise.
          const target = slides.reduce(
            (best, el) => (Math.abs(el.offsetLeft - list.scrollLeft) < Math.abs(best - list.scrollLeft) ? el.offsetLeft : best),
            slides[0].offsetLeft
          );
          if (Math.abs(list.scrollLeft - target) > 0.5) list.scrollTo({ left: target, behavior: 'smooth' });
        }, 140);
      },
      { passive: true }
    );
  };

  const setupGalleryBar = (modal) => {
    const list = modal.querySelector('.product__media-list');
    if (!list || list.children.length < 2) return;
    settleGallery(list);
    const host = list.closest('slider-component') || list.parentElement;
    if (!host || host.querySelector('.t810-railbar')) return;

    const bar = document.createElement('div');
    bar.className = 't810-railbar';
    bar.setAttribute('aria-hidden', 'true');
    const thumb = document.createElement('span');
    bar.appendChild(thumb);
    host.insertAdjacentElement('afterend', bar);

    const sync = () => {
      const span = list.scrollWidth - list.clientWidth;
      const ratio = list.clientWidth / list.scrollWidth;
      thumb.style.width = `${Math.max(12, ratio * 100)}%`;
      thumb.style.left = span > 0 ? `${(list.scrollLeft / span) * (100 - Math.max(12, ratio * 100))}%` : '0%';
    };
    list.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  };

  const watch = (modal) => {
    if (modal.dataset.t810Watched) return;
    modal.dataset.t810Watched = 'true';
    new MutationObserver(() => {
      setLock(anyOpen());
      if (modal.hasAttribute('open')) {
        watchScroll(modal);
        setupGalleryBar(modal);
      }
    }).observe(modal, {
      attributes: true,
      attributeFilter: ['open'],
    });
  };

  const scan = () => document.querySelectorAll('quick-add-modal').forEach(watch);

  document.addEventListener('DOMContentLoaded', () => {
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
    // The product page carousel needs the same settle on phones.
    if (window.matchMedia('(max-width: 749px)').matches) {
      document.querySelectorAll('#MainContent .product__media-list').forEach(settleGallery);
    }
  });
})();
